import {
  type AdminLegalLinkCheck,
  adminLegalLinkCheckRequestSchema,
  adminUpdateBrandingRequestSchema,
} from "@ternary/contracts"
import { Checkbox, Field, Input } from "@ternary/ui"
import { useState } from "react"
import { checkLegalLink } from "../api.js"

/** A branding draft's legal: the two links, and whether Ternary's standard terms replace them. */
export type LegalDraft = { privacyUrl: string; standardLegal: boolean; termsUrl: string }

type LinkKey = "privacyUrl" | "termsUrl"

/** The link check's advice. It says the link could not be opened, never that it is broken. */
export const LINK_WARNING = "We couldn't open this link. Check it before you publish."

/**
 * The advice when the host refused the check itself (`reason: "unconfirmed"`, a 403 or 405 HEAD):
 * the page may well open in a browser (Task 16 M10b).
 */
export const UNCONFIRMED_WARNING =
  "We couldn't confirm this link. Check it opens before you publish."

/**
 * Whether the standard terms start on: only while they are available (Ruling 7), when the business
 * chose them or has neither link yet. While they are off, a choice saved earlier is not offered.
 */
export function initialStandardLegal(saved: LegalDraft, standardAvailable: boolean) {
  return (
    standardAvailable &&
    (saved.standardLegal || (!saved.termsUrl.trim() && !saved.privacyUrl.trim()))
  )
}

/** A link that the branding update takes as a legal link: http(s). */
export function isLegalLink(text: string) {
  return adminUpdateBrandingRequestSchema.shape.termsUrl.safeParse(text).success
}

/** The message for a link that is not http(s), wherever the admin asks for one. */
export const LINK_MESSAGE = "Enter an http:// or https:// link."

/**
 * The terms link a branding save sends while the standard terms are on: the saved one, kept as it
 * is, or null (none) when the contract refuses it, so a hidden field never blocks the save.
 */
export function keptTermsUrl(saved: string): string | null {
  return isLegalLink(saved) ? saved : null
}

/**
 * The links a branding update sends, for Settings and Basics' dialog alike. With Ternary's standard
 * terms chosen (and available), the saved links stay: the terms link as `keptTermsUrl` keeps it,
 * and no privacy link, which keeps the saved one. Otherwise the typed links, trimmed: an empty
 * privacy link removes the saved one (null) or, with none saved, is left out.
 */
export function legalSaveFields(
  draft: LegalDraft,
  saved: { privacyUrl: string; termsUrl: string },
  standardAvailable: boolean,
): { privacyUrl?: string | null; termsUrl: string | null } {
  if (standardAvailable && draft.standardLegal) return { termsUrl: keptTermsUrl(saved.termsUrl) }
  const privacyUrl = draft.privacyUrl.trim()
  return {
    ...(privacyUrl ? { privacyUrl } : saved.privacyUrl ? { privacyUrl: null } : {}),
    termsUrl: draft.termsUrl.trim(),
  }
}

/**
 * The terms and privacy links. While Ternary's standard terms are available (`standardAvailable`,
 * TENANT_STANDARD_LEGAL_ENABLED), a checkbox chooses them instead, hiding both fields, and each
 * filled link is checked when it loses focus: one that cannot be opened shows `LINK_WARNING`, and
 * one whose host refused the check shows `UNCONFIRMED_WARNING`.
 * The check is advice: it never blocks a save, and a check the server refuses shows nothing.
 * Otherwise it is today's two fields, with no check and no request.
 */
export function LegalLinks({
  companyName,
  disabled = false,
  draft,
  errors,
  onChange,
  privacyHint,
  privacyRequired = false,
  standardAvailable,
}: {
  companyName: string
  disabled?: boolean
  draft: LegalDraft
  errors: { privacyUrl?: string | undefined; termsUrl?: string | undefined }
  onChange(patch: Partial<LegalDraft>): void
  /** Under the privacy link while the standard terms are available, unless advice replaces it. */
  privacyHint?: string | undefined
  /**
   * The privacy link is required too, as where a signup page must publish (Basics' dialog). Off,
   * it is optional, as Brand and Settings have always had it.
   */
  privacyRequired?: boolean
  standardAvailable: boolean
}) {
  // Each field's last link asked about, and its answer: "asking" while one is on its way, and
  // "refused" when the server would not check it. Once the field changes, it is stale.
  const [checked, setChecked] = useState<
    Partial<Record<LinkKey, { answer: AdminLegalLinkCheck | "asking" | "refused"; url: string }>>
  >({})

  function check(key: LinkKey) {
    const url = draft[key].trim()
    // One request per link: answered, refused or on its way, it is not asked about again.
    if (checked[key]?.url === url) return
    if (!adminLegalLinkCheckRequestSchema.safeParse({ url }).success) return
    const settle = (answer: AdminLegalLinkCheck | "asking" | "refused") =>
      setChecked((current) =>
        // An older link's answer never replaces a newer one's.
        answer !== "asking" && current[key]?.url !== url
          ? current
          : { ...current, [key]: { answer, url } },
      )
    settle("asking")
    checkLegalLink(url).then(
      (answer) => settle(answer),
      // Refused (401, 429, or the route's 404 while it is off): no advice, and no error.
      () => settle("refused"),
    )
  }

  function advice(key: LinkKey) {
    const last = checked[key]
    if (!last || last.url !== draft[key].trim() || typeof last.answer === "string") return
    if (last.answer.reachable) return
    return last.answer.reason === "unconfirmed" ? UNCONFIRMED_WARNING : LINK_WARNING
  }

  const field = (key: LinkKey, label: string, hint?: string) => (
    <Field
      error={errors[key]}
      label={label}
      required={!disabled && (key === "termsUrl" || privacyRequired)}
      // The hint is a live region, so advice that arrives after the focus moved on is announced.
      {...(standardAvailable
        ? { hint: errors[key] ? undefined : (advice(key) ?? hint), liveHint: true }
        : {})}
    >
      <Input
        disabled={disabled}
        inputMode="url"
        onChange={(event) => onChange({ [key]: event.currentTarget.value })}
        // Off, Settings' fields stay exactly as they were. On, a link field like Brand's contact
        // link: a URL to fill in, never spell-checked.
        {...(standardAvailable
          ? { autoComplete: "url", onBlur: () => check(key), spellCheck: false }
          : {})}
        type="url"
        value={draft[key]}
      />
    </Field>
  )

  const links = (
    <div className="admin-legal-links">
      {field("termsUrl", "Terms of Service link")}
      {field("privacyUrl", "Privacy policy link", privacyHint)}
    </div>
  )
  if (!standardAvailable) return links
  return (
    <div className="admin-legal-choice">
      {/* A checkbox, as it looks: the kit has no switch, and a tick box read out as a switch
          would say one thing and show another. */}
      <Checkbox
        checked={draft.standardLegal}
        disabled={disabled}
        label={`Use Ternary's standard terms and privacy for ${companyName.trim() || "your company"}`}
        onChange={(event) => onChange({ standardLegal: event.currentTarget.checked })}
      />
      {draft.standardLegal ? null : links}
    </div>
  )
}
